import {
  CSSProperties,
  memo,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Icon, IconButton, List, Menu, Progress, Tooltip } from '@atom/mui';
import {
  AddIcon,
  ArrowForwardIcon,
  AutorenewIcon,
  CheckIcon,
  ClearIcon,
  DeleteIcon,
  EditIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetElement, ChangeType } from '@atom/types/asset';
import {
  hasOwnChange,
  isElementsChanged,
} from '@atom/utilities/assetUtilities';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import iconUtilities from '@atom/utilities/iconUtilities';

import {
  SubItemModalType,
  SubItemPermissions,
  SubItemRowPolicy,
} from './SubItemsContext';

import classes from './subItemTree.module.css';

const { ListItemButton, ListItemText } = List;
const { MenuItem } = Menu;

const styles = {
  listItemText: {
    marginLeft: '0.625rem',
  },
};

const MAX_SCROLL_WAIT_FRAMES = 120;

// Nearest ancestor MUI Collapse still animating open.
const getTransitioningCollapse = (
  element: HTMLElement | null,
): HTMLElement | null => {
  const collapse =
    element?.parentElement?.closest<HTMLElement>('.MuiCollapse-root');

  if (!collapse) {
    return null;
  }

  return collapse.style.height && collapse.style.height !== 'auto'
    ? collapse
    : getTransitioningCollapse(collapse);
};

const getRowStyle = (depth: number, isSelected: boolean): CSSProperties => ({
  boxSizing: 'border-box',
  backgroundColor: isSelected ? colors.utility.highlight : undefined,
  borderLeft: isSelected
    ? `3px solid ${colors.brand.blue}`
    : '3px solid transparent',
  paddingLeft: `${depth * 1.5}rem`,
});

const getAttributeSubText = (node: AssetElement): string =>
  (node.attributes ?? [])
    .filter(attribute => attribute.isVisibleAsSubtext)
    .map(
      attribute =>
        `${attribute.name}: ${attributeDisplayUtility.display(
          attribute.value,
          attribute.dataType,
          attribute.unit,
        )}`,
    )
    .join(', ');

const getSubText = (node: AssetElement, rowPolicy: SubItemRowPolicy): string =>
  [rowPolicy.subtextPrefix?.(node) ?? '', getAttributeSubText(node)]
    .filter(part => !R.isEmpty(part))
    .join(' \u00B7 ');

const getLabelClass = (changeType?: ChangeType): string =>
  clsx(classes.elementLabel, {
    [classes.elementLabelDeleted]: changeType === ChangeType.DELETED,
    [classes.elementLabelPending]: hasOwnChange(changeType),
  });

type SubItemRowProps = {
  node: AssetElement;
  depth: number;
  hasDownstreamChanges: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  shouldScroll: boolean;
  permissions: SubItemPermissions;
  rowPolicy: SubItemRowPolicy;
  // Replaces the element marker icon, e.g. the root asset's schema icon.
  customIcon?: ReactNode;
  // False for a row whose children are always shown (the root row); it gets
  // no expand arrow.
  expandable?: boolean;
  onClick: (node: AssetElement) => void;
  onApprove: (node: AssetElement) => void;
  onOpenModal: (type: SubItemModalType, node: AssetElement) => void;
  onScrolled: () => void;
};

const SubItemRow = ({
  node,
  depth,
  hasDownstreamChanges,
  isSelected,
  isExpanded,
  isLoading,
  shouldScroll,
  permissions,
  rowPolicy,
  customIcon,
  expandable = true,
  onClick,
  onApprove,
  onOpenModal,
  onScrolled,
}: SubItemRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);

  // Scroll the deep-linked row into view once its position is final.
  useEffect(() => {
    if (!shouldScroll) {
      return undefined;
    }

    let frame: number;
    let remainingFrames = MAX_SCROLL_WAIT_FRAMES;

    const scrollWhenSettled = () => {
      if (getTransitioningCollapse(rowRef.current) && remainingFrames > 0) {
        remainingFrames -= 1;
        frame = requestAnimationFrame(scrollWhenSettled);
        return;
      }

      rowRef.current?.scrollIntoView({ block: 'center' });
      onScrolled();
    };

    frame = requestAnimationFrame(scrollWhenSettled);
    return () => cancelAnimationFrame(frame);
  }, [shouldScroll]);

  const {
    canUpdate,
    canRename,
    canManageChanges,
    moveEnabled,
    replaceEnabled,
  } = permissions;

  const isPending = Boolean(node.changeType);
  const ownChange = hasOwnChange(node.changeType);
  const hasPendingDelete = node.changeType === ChangeType.DELETED;
  const showRename = canRename && !hasPendingDelete;
  const showAdd = canUpdate && rowPolicy.canAddChildren(node);
  const showDelete = canUpdate && rowPolicy.canDelete(node);
  const showMove = moveEnabled && node.changeType !== ChangeType.ADDED;
  const showReplace = replaceEnabled && node.changeType !== ChangeType.ADDED;
  const showOptions =
    showAdd || showRename || showDelete || showMove || showReplace;
  const showChangeControls =
    canManageChanges &&
    isPending &&
    !isElementsChanged(node.changeType) &&
    node.changeType !== ChangeType.PARENT_ADDED;

  const subText = getSubText(node, rowPolicy);
  const arrowType = isExpanded ? 'down-arrow' : 'right-arrow';
  const showArrow = expandable && node.hasChildren;

  const customRowIcon = showArrow ? (
    <div className={classes.customIconExpandable}>
      {iconUtilities.leftIcon(arrowType)}
      <div className={classes.customIcon}>{customIcon}</div>
    </div>
  ) : (
    <div className={clsx(classes.customIcon, classes.customIconLeaf)}>
      {customIcon}
    </div>
  );

  const markerIcon = showArrow
    ? iconUtilities.leftElementIcon(node.markerId, arrowType, ownChange)
    : iconUtilities.getElementIcon(node.markerId, ownChange);

  const icon = customIcon ? customRowIcon : markerIcon;

  return (
    <div ref={rowRef}>
      <ListItemButton
        disableGutters
        style={getRowStyle(depth, isSelected)}
        data-cy={`elementListItem-${node.name}`}
        onClick={() => onClick(node)}
      >
        <div className={classes.iconContainer}>
          {icon}
          {hasDownstreamChanges && (
            <div
              className={classes.changeIndicator}
              data-cy={`elementChangeIndicator-${node.name}`}
            />
          )}
        </div>
        <ListItemText
          primaryTextStyle={styles.listItemText}
          primary={
            <div className={classes.elementNameContainer}>
              <div
                className={getLabelClass(node.changeType)}
                data-cy={`element-${node.name}-${node.id}`}
              >
                {node.name}
              </div>
              {!R.isEmpty(subText) && (
                <Tooltip
                  lightTooltip
                  title={subText.replace(/,/g, ';')}
                  placement="right-start"
                >
                  <div className={classes.elementSubText}>{subText}</div>
                </Tooltip>
              )}
            </div>
          }
        />
        <div className={classes.rowControls}>
          {isLoading && <Progress size={18} thickness={2} />}
          {showChangeControls && (
            <div className={classes.pendingControls}>
              <div className={classes.pendingLabel}>{node.changeType}</div>
              <IconButton
                tooltip="Reject"
                data-cy={`elementRejectButton-${node.name}`}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  onOpenModal(SubItemModalType.REJECT_CHANGES, node);
                }}
              >
                <Icon color={colors.brand.blue} icon={ClearIcon} />
              </IconButton>
              <IconButton
                tooltip="Approve"
                data-cy={`elementApproveButton-${node.name}`}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  onApprove(node);
                }}
              >
                <Icon color={colors.brand.blue} icon={CheckIcon} />
              </IconButton>
            </div>
          )}
          {showOptions && (
            <Menu
              dataCyLabel={`subItemMenu-${node.name}-${node.id}`}
              keepMounted={false}
            >
              {showAdd && (
                <MenuItem
                  startAdornment={<Icon icon={AddIcon} />}
                  data-cy={`addElement-${node.name}-${node.id}`}
                  onClick={() =>
                    onOpenModal(SubItemModalType.ADD_ELEMENT, node)
                  }
                >
                  Add Element
                </MenuItem>
              )}
              {showMove && (
                <MenuItem
                  startAdornment={<Icon icon={ArrowForwardIcon} />}
                  data-cy={`move-${node.name}-${node.id}`}
                  onClick={() => onOpenModal(SubItemModalType.MOVE, node)}
                >
                  Move
                </MenuItem>
              )}
              {showReplace && (
                <MenuItem
                  startAdornment={<Icon icon={AutorenewIcon} />}
                  data-cy={`replace-${node.name}-${node.id}`}
                  onClick={() => onOpenModal(SubItemModalType.REPLACE, node)}
                >
                  Replace
                </MenuItem>
              )}
              {showRename && (
                <MenuItem
                  startAdornment={<Icon icon={EditIcon} />}
                  data-cy={`edit-${node.name}-${node.id}`}
                  onClick={() => onOpenModal(SubItemModalType.RENAME, node)}
                >
                  Edit
                </MenuItem>
              )}
              {showDelete && (
                <MenuItem
                  startAdornment={<Icon icon={DeleteIcon} />}
                  data-cy={`delete-${node.name}-${node.id}`}
                  onClick={() => onOpenModal(SubItemModalType.DELETE, node)}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          )}
        </div>
      </ListItemButton>
    </div>
  );
};

export default memo(SubItemRow);
